import React from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { CONSTANTS } from "../../constants";

// Conditionally import WebView only for native
let WebView: any = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}

interface CartDeliveryMapProps {
  userLocation?: {
    lat: number | string;
    lng: number | string;
  };
  deliveryAddress?: string;
}

export default function CartDeliveryMap({
  userLocation,
  deliveryAddress,
}: CartDeliveryMapProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const apiKey = CONSTANTS.GOOGLE_MAPS_API_KEY;

  // Log the received data for debugging
  console.log("🗺️ [CART-DELIVERY-MAP] Received props:", {
    userLocation: JSON.stringify(userLocation),
    deliveryAddress: deliveryAddress,
    hasApiKey: !!apiKey,
  });

  // Check if we have valid, usable coordinates
  const hasValidLat =
    userLocation?.lat !== null &&
    userLocation?.lat !== undefined &&
    !isNaN(Number(userLocation?.lat));
  const hasValidLng =
    userLocation?.lng !== null &&
    userLocation?.lng !== undefined &&
    !isNaN(Number(userLocation?.lng));

  // Use the provided coordinates if valid, otherwise use default coordinates
  const mapLat = hasValidLat
    ? Number(userLocation.lat)
    : CONSTANTS.MAP_CONFIG.DEFAULT_LOCATION.lat;
  const mapLng = hasValidLng
    ? Number(userLocation.lng)
    : CONSTANTS.MAP_CONFIG.DEFAULT_LOCATION.lng;

  console.log("🗺️ [CART-DELIVERY-MAP] Final coordinates:", {
    lat: mapLat,
    lng: mapLng,
    usingFallback: !hasValidLat || !hasValidLng,
  });

  // Create HTML content for the interactive Google Map
  const mapHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body, html { 
            margin: 0; 
            padding: 0; 
            height: 100%; 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
          }
          #map { 
            height: 100%; 
            width: 100%; 
          }
          .custom-info-window {
            font-size: 14px;
            color: ${colors.text};
            max-width: 200px;
          }
          .info-title {
            font-weight: 600;
            color: ${colors.primary};
            margin-bottom: 4px;
          }
          .info-address {
            color: ${colors.textLight};
            line-height: 1.4;
          }
        </style>
      </head>
      <body>
        <div id="map"></div>
        <script>
          function initMap() {
            const location = { lat: ${mapLat}, lng: ${mapLng} };
            
            const map = new google.maps.Map(document.getElementById("map"), {
              zoom: ${CONSTANTS.MAP_CONFIG.DEFAULT_ZOOM},
              center: location,
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }]
                }
              ],
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT
              }
            });

            const marker = new google.maps.Marker({
              position: location,
              map: map,
              title: "Delivery Location",
              icon: {
                url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDOC4xMyAyIDUgNS4xMyA1IDlDNSAxNC4yNSAxMiAyMiAxMiAyMkMxMiAyMiAxOSAxNC4yNSAxOSA5QzE5IDUuMTMgMTUuODcgMiAxMiAyWk0xMiAxMS41QzEwLjYyIDExLjUgOS41IDEwLjM4IDkuNSA5QzkuNSA3LjYyIDEwLjYyIDYuNSAxMiA2LjVDMTMuMzggNi41IDE0LjUgNy42MiAxNC41IDlDMTQuNSAxMC4zOCAxMy4zOCAxMS41IDEyIDExLjVaIiBmaWxsPSIjRUM0ODk5Ii8+Cjwvc3ZnPgo=',
                scaledSize: new google.maps.Size(32, 32),
                anchor: new google.maps.Point(16, 32)
              }
            });

            const infoWindow = new google.maps.InfoWindow({
              content: \`
                <div class="custom-info-window">
                  <div class="info-title">Your Delivery Address</div>
                  <div class="info-address">${
                    deliveryAddress || "Set Delivery Address"
                  }</div>
                  ${
                    !hasValidLat || !hasValidLng
                      ? `<div style="color: ${colors.warning}; font-size: 12px; margin-top: 4px;">⚠️ Using default location</div>`
                      : ""
                  }
                </div>
              \`
            });

            marker.addListener("click", () => {
              infoWindow.open(map, marker);
            });

            // Auto-open info window after a short delay
            setTimeout(() => {
              infoWindow.open(map, marker);
            }, 1000);
          }

          window.initMap = initMap;
        </script>
        <script async defer src="https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap"></script>
      </body>
    </html>
  `;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Your Delivery Address</Text>
      </View>

      {deliveryAddress && deliveryAddress !== "Set Delivery Address" && (
        <View style={styles.addressContainer}>
          <Text style={styles.addressText}>{deliveryAddress}</Text>
          {(!hasValidLat || !hasValidLng) && (
            <Text style={styles.warningText}>⚠️ Using default location</Text>
          )}
        </View>
      )}

      {(!deliveryAddress || deliveryAddress === "Set Delivery Address") && (
        <View style={styles.addressContainer}>
          <Text style={styles.placeholderText}>
            Please set your delivery address
          </Text>
        </View>
      )}

      <View style={styles.mapContainer}>
        {apiKey ? (
          Platform.OS === 'web' ? (
            // Web: Use iframe with Google Maps Embed API
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(deliveryAddress || `${mapLat},${mapLng}`)}&center=${mapLat},${mapLng}&zoom=${CONSTANTS.MAP_CONFIG.DEFAULT_ZOOM}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 8,
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            // Native: Use WebView
            WebView && (
              <WebView
                source={{ html: mapHTML }}
                style={styles.map}
                javaScriptEnabled={true}
                domStorageEnabled={true}
                startInLoadingState={true}
                mixedContentMode="compatibility"
                allowsInlineMediaPlayback={true}
                mediaPlaybackRequiresUserAction={false}
                scalesPageToFit={true}
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
                onError={(syntheticEvent) => {
                  const { nativeEvent } = syntheticEvent;
                  console.warn("WebView error: ", nativeEvent);
                }}
              />
            )
          )
        ) : (
          <View style={styles.fallbackContainer}>
            <Ionicons name="map-outline" size={48} color={colors.textLight} />
            <Text style={styles.fallbackText}>Map not available</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      marginVertical: 16,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
      borderWidth: 1,
      borderColor: colors.border,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      paddingBottom: 6,
      backgroundColor: colors.backgroundSecondary,
    },
    icon: {
      marginRight: 8,
    },
    title: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
    },
    addressContainer: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      backgroundColor: colors.backgroundSecondary,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    addressText: {
      fontSize: 14,
      color: colors.textLight,
    },
    warningText: {
      fontSize: 12,
      color: colors.warning,
      marginTop: 2,
      fontStyle: "italic",
    },
    placeholderText: {
      fontSize: 14,
      color: colors.textLight,
      fontStyle: "italic",
    },
    mapContainer: {
      width: "100%",
      height: 200,
      overflow: "hidden",
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    map: {
      width: "100%",
      height: "100%",
    },
    fallbackContainer: {
      width: "100%",
      height: "100%",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
    },
    fallbackText: {
      marginTop: 8,
      color: colors.textLight,
      fontSize: 14,
    },
  });
